/**
 * Setup — first run.
 *
 * ── What "first run" means here ──────────────────────────────────────────────
 *
 * The application is fully usable on the day it is installed, and every number
 * in it is a deliberately conservative placeholder with `verified_on = NULL`.
 * That is the honest state, not a bug — but it is only honest if it is SAID.
 * `GET /api/setup` returns exactly that list: every assumption nobody has
 * checked, every land measurement nobody has taken, every setting still on its
 * built-in fallback. This screen leads with it.
 *
 * ── Two forms, and pointers for the rest ─────────────────────────────────────
 *
 * The two that have to be done before anything else works properly are here:
 * the LAND, because an unmeasured parcel disables every access warning, and
 * the SEARCH PROFILE, because the target and ceiling drive the heaviest factor
 * in the score. Scoring weights, cost assumptions, diligence templates,
 * sources and notification policy are configuration surfaces of their own; the
 * warnings list names them and says where each one is edited, using the
 * server's own `editedAt` map rather than a second copy of it here.
 *
 * ── The route ────────────────────────────────────────────────────────────────
 *
 * `/setup` is the index and `/setup/:section` picks a panel — `route.ts`
 * parses both into one route, because the section is a panel choice rather
 * than a different screen.
 */

import { useCallback, useEffect, useState } from 'react'
import { get } from '../../lib/api'
import { useLocale, type Locale } from '../../lib/i18n'
import { navigate, useRoute } from '../../lib/route'
import Icon from '../../components/Icon'
import { dateText, TONES } from '../../lib/format'
import { humanise } from '../../lib/domain'
import { Banner, Chip, S as PS } from '../../components/detail/ui'
import LandProfileForm from './LandProfileForm'
import SearchProfileForm from './SearchProfileForm'

/* ────────────────────────────────────────────────────────────────────────────
 * Server shapes
 * ────────────────────────────────────────────────────────────────────────── */

interface ConfigWarning {
  kind: 'unverified_assumption' | 'unmeasured_land' | 'unseeded_setting' | 'source_unhealthy' | 'no_ai'
  detail: string
  /** Where a person goes to fix it, as the server states it. */
  where: string
}

interface SetupOverview {
  environment: string
  today: string
  timezone: string
  warnings: ConfigWarning[]
  landProfiles: { id: string; name: string; is_default: number; updated_at: number }[]
  searchProfile: { id: string; name: string } | null
  editedAt: Record<string, string>
}

/* ────────────────────────────────────────────────────────────────────────────
 * Copy
 * ────────────────────────────────────────────────────────────────────────── */

const EN = {
  title: 'Setup',
  intro:
    'This application ships with conservative placeholders rather than blanks, so it works on day one. Everything below is a number nobody has confirmed yet — usable, and honestly labelled until somebody checks it.',
  loading: 'Loading…',
  failed: 'Setup could not be loaded.',
  sectionLand: 'The land',
  sectionSearch: 'What we are looking for',
  sectionOverview: 'What still needs checking',
  clean: 'Nothing is waiting to be verified.',
  warnings: 'to verify',
  fixIn: 'Edited at',
  landCta: 'Measure the land',
  searchCta: 'Set the budget',
  today: 'Today, where the land is',
  kind_unverified_assumption: 'A number nobody has checked',
  kind_unmeasured_land: 'A measurement nobody has taken',
  kind_unseeded_setting: 'A setting still on its built-in fallback',
  kind_source_unhealthy: 'A source that is not answering',
  kind_no_ai: 'AI is off',
  back: 'All of setup',
} as const

type CopyKey = keyof typeof EN

const ES: Record<CopyKey, string> = {
  title: 'Configuración',
  intro:
    'Esta aplicación viene con valores provisionales conservadores en vez de espacios en blanco, así que funciona desde el primer día. Todo lo de abajo es un número que nadie ha confirmado — se puede usar, y está marcado con honestidad hasta que alguien lo compruebe.',
  loading: 'Cargando…',
  failed: 'No se pudo cargar la configuración.',
  sectionLand: 'El terreno',
  sectionSearch: 'Lo que estamos buscando',
  sectionOverview: 'Lo que falta por comprobar',
  clean: 'No hay nada esperando por verificación.',
  warnings: 'por verificar',
  fixIn: 'Se edita en',
  landCta: 'Medir el terreno',
  searchCta: 'Poner el presupuesto',
  today: 'Hoy, donde está el terreno',
  kind_unverified_assumption: 'Un número que nadie ha comprobado',
  kind_unmeasured_land: 'Una medida que nadie ha tomado',
  kind_unseeded_setting: 'Un ajuste todavía en su valor de respaldo',
  kind_source_unhealthy: 'Una fuente que no contesta',
  kind_no_ai: 'La IA está apagada',
  back: 'Toda la configuración',
}

const COPY: Record<Locale, Record<CopyKey, string>> = { en: EN, 'es-PR': ES }

const S = {
  page: { padding: 12, paddingBottom: 96, maxWidth: 720, margin: '0 auto' } as const,
  tile: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    minHeight: 60,
    padding: '0 14px',
    marginBottom: 8,
    borderRadius: 'var(--radius)',
    border: '1px solid var(--line)',
    background: 'var(--surface)',
    color: 'var(--ink)',
    font: 'inherit',
    fontSize: 16,
    fontWeight: 700,
    textAlign: 'left',
    cursor: 'pointer',
  } as const,
  warning: { padding: '10px 0', borderTop: '1px solid var(--line)' } as const,
}

/* ────────────────────────────────────────────────────────────────────────────
 * The screen
 * ────────────────────────────────────────────────────────────────────────── */

export default function Setup({ section: propSection }: { section?: string } = {}) {
  const locale = useLocale()
  const t = useCallback((key: CopyKey) => COPY[locale][key] ?? EN[key], [locale])
  const route = useRoute()
  const section = propSection ?? route.params.section ?? ''

  const [overview, setOverview] = useState<SetupOverview | null>(null)
  const [state, setState] = useState<'loading' | 'ready' | 'failed'>('loading')

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const body = await get<SetupOverview>('/api/setup')
        if (!cancelled) {
          setOverview(body)
          setState('ready')
        }
      } catch {
        // `/api/setup` is `settings.write` and answers 404 to everyone else, so
        // a failure here is either "not allowed" or "offline" — and neither is
        // a reason to hide the two forms, which fail on their own terms.
        if (!cancelled) setState('failed')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  if (section === 'land') {
    return (
      <div style={S.page}>
        <BackLink label={t('back')} />
        <LandProfileForm />
      </div>
    )
  }

  if (section === 'search') {
    return (
      <div style={S.page}>
        <BackLink label={t('back')} />
        <SearchProfileForm />
      </div>
    )
  }

  const warnings = overview?.warnings ?? []

  return (
    <div style={S.page}>
      <h1 style={{ fontSize: 22, fontWeight: 800, margin: '4px 0 8px' }}>{t('title')}</h1>
      <p style={PS.note}>{t('intro')}</p>

      <div style={{ marginTop: 16 }}>
        <button type="button" style={S.tile} onClick={() => navigate('/setup/land')}>
          <Icon name="pin" size={20} />
          <span style={{ flex: 1 }}>
            {t('sectionLand')}
            <span style={{ display: 'block', fontSize: 12, fontWeight: 400, color: 'var(--muted)' }}>
              {t('landCta')}
            </span>
          </span>
          <Icon name="chevron" size={18} />
        </button>

        <button type="button" style={S.tile} onClick={() => navigate('/setup/search')}>
          <Icon name="search" size={20} />
          <span style={{ flex: 1 }}>
            {t('sectionSearch')}
            <span style={{ display: 'block', fontSize: 12, fontWeight: 400, color: 'var(--muted)' }}>
              {t('searchCta')}
            </span>
          </span>
          <Icon name="chevron" size={18} />
        </button>
      </div>

      <h2 style={{ fontSize: 16, fontWeight: 800, margin: '22px 0 4px' }}>
        {t('sectionOverview')}
        {warnings.length > 0 && (
          <span style={{ marginLeft: 8 }}>
            <Chip tone={TONES.warn}>
              {warnings.length} {t('warnings')}
            </Chip>
          </span>
        )}
      </h2>

      {state === 'loading' && <p style={PS.note}>{t('loading')}</p>}
      {state === 'failed' && (
        <Banner tone={TONES.quiet} icon="info" outlined>
          {t('failed')}
        </Banner>
      )}

      {state === 'ready' && warnings.length === 0 && (
        <Banner tone={TONES.strong} icon="check">
          {t('clean')}
        </Banner>
      )}

      {warnings.map((warning, index) => (
        <div key={`${warning.kind}-${index}`} style={S.warning}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)' }}>
            {t(`kind_${warning.kind}` as CopyKey)}
          </div>
          <p style={{ ...PS.sentence, marginTop: 2 }}>{warning.detail}</p>
          {/* The server states where the save goes. Repeating that map here
              would be a second copy that drifts the first time a route moves. */}
          <p style={{ ...PS.note, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 11 }}>
            {t('fixIn')}: {warning.where}
          </p>
        </div>
      ))}

      {overview && (
        <p style={{ ...PS.note, marginTop: 16 }}>
          {t('today')}: {dateText(overview.today, locale)} · {overview.timezone} · {humanise(overview.environment)}
        </p>
      )}
    </div>
  )
}

function BackLink({ label }: { label: string }) {
  return (
    <button
      type="button"
      onClick={() => navigate('/setup')}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        minHeight: 44,
        padding: 0,
        background: 'transparent',
        border: 'none',
        color: 'var(--muted)',
        font: 'inherit',
        fontSize: 14,
        cursor: 'pointer',
        marginBottom: 6,
      }}
    >
      <Icon name="back" size={16} />
      {label}
    </button>
  )
}
